import React, { useState, useEffect, useCallback } from 'react';
import Icon from './AppIcon';

/**
 * PWAInstallBadge
 * Shows an "Install App" prompt when the browser fires the
 * `beforeinstallprompt` event (Chrome / Edge / Android).
 * On iOS Safari it shows a manual "Add to Home Screen" tip.
 */
const PWAInstallBadge = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBadge, setShowBadge] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed or running as standalone PWA
    const wasDismissed = sessionStorage.getItem('ks_pwa_dismissed');
    if (wasDismissed) return;

    const isStandalone =
      window.matchMedia('(display-mode: standalone)')?.matches ||
      window.navigator?.standalone === true;
    if (isStandalone) return;

    // iOS detection
    const ios =
      /iphone|ipad|ipod/i?.test(navigator.userAgent) &&
      !window.MSStream;
    setIsIOS(ios);

    if (ios) {
      // Show iOS tip after a short delay
      const t = setTimeout(() => setShowBadge(true), 3000);
      return () => clearTimeout(t);
    }

    // Chrome / Edge / Android — listen for native prompt
    const handler = (e) => {
      e?.preventDefault();
      setDeferredPrompt(e);
      setShowBadge(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Hide if user installs via browser UI
    window.addEventListener('appinstalled', () => {
      setInstalled(true);
      setShowBadge(false);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt?.prompt();
    const { outcome } = await deferredPrompt?.userChoice;
    if (outcome === 'accepted') {
      setInstalled(true);
      setShowBadge(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    setShowBadge(false);
    sessionStorage.setItem('ks_pwa_dismissed', '1');
  }, []);

  if (!showBadge || dismissed || installed) return null;

  return (
    <div
      role="dialog"
      aria-label="Installer l'application"
      className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-sm"
    >
      <div className="bg-white dark:bg-zinc-900 border border-border/60 rounded-2xl shadow-2xl shadow-black/20 overflow-hidden">
        {/* Gold accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-brand-gold via-yellow-400 to-brand-gold" />

        <div className="p-4 flex items-start gap-3">
          {/* App icon */}
          <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-black flex items-center justify-center shadow-md">
            <img
              src="/assets/images/Logo_d_immeuble_moderne_en_or-1758887759495.png"
              alt="Kapital States"
              className="w-10 h-10 object-contain"
            />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-text-primary leading-tight">
              Installer Kapital States
            </p>
            {isIOS ? (
              <p className="text-xs text-text-secondary mt-1 leading-snug">
                Appuyez sur{' '}
                <Icon name="Share" size={12} className="inline-block align-middle mx-0.5 text-blue-500" />
                {' '}puis <strong>« Sur l'écran d'accueil »</strong> pour installer l'app.
              </p>
            ) : (
              <p className="text-xs text-text-secondary mt-1 leading-snug">
                Accès rapide, mode hors-ligne et expérience mobile optimale.
              </p>
            )}

            {!isIOS && (
              <button
                onClick={handleInstall}
                className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-gold text-white text-xs font-semibold rounded-lg hover:bg-brand-gold/90 active:scale-95 transition-all duration-150"
              >
                <Icon name="Download" size={13} />
                Installer l'app
              </button>
            )}
          </div>

          {/* Dismiss */}
          <button
            onClick={handleDismiss}
            aria-label="Fermer"
            className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full hover:bg-surface transition-colors"
          >
            <Icon name="X" size={14} className="text-text-secondary" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallBadge;
